import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useState } from "react";
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { MessageCircle, Send } from "lucide-react"
import customerService from "@/service/customer.service";

export function RequirementsSection() {

  const [isContactFormOpen, setIsContactFormOpen] = useState(false)
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleContactSubmit = async () => {
    setIsSubmitting(true)
    try {
      const payload = {
        name: contactForm.name,
        email: contactForm.email,
        phone_number: contactForm.phone,
        requirements: contactForm.message,
      }

      await customerService.createLeadRequest(payload)

      // Reset form and close modal
      setContactForm({ name: "", email: "", phone: "", message: "" })
      setIsContactFormOpen(false)

      alert("✅ Thank you! Your requirements have been submitted successfully.")
    } catch (error) {
      console.error("Error submitting form:", error)
      alert("Sorry, there was an error submitting your requirements. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const isContactFormValid = contactForm.name && contactForm.email && contactForm.phone && contactForm.message

  return (
    <section id="professionals" className="bg-gray-50 pb-8">
      {/* Did not find what you want Section - Compact Design */}
      <div className="w-full">
        <div className="hbn-gradient py-12 md:py-16">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <div className="mb-4">
                <MessageCircle className="w-12 h-12 text-white mx-auto mb-3" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Did not find what you want?
              </h3>
              <p className="text-lg text-white/90 mb-6 max-w-2xl mx-auto">
                Can't find the information you're looking for? Share your
                requirements and we'll help you within 24 hours.
              </p>
  
              <Dialog open={isContactFormOpen} onOpenChange={setIsContactFormOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white px-6 py-3 text-lg font-semibold rounded-lg shadow-md hover:shadow-lg transition-transform transform hover:scale-105">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Share Your Requirements
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg w-full mx-4 mt-10 max-h-[80vh] overflow-y-auto rounded-xl shadow-xl">
                  <DialogHeader className="space-y-4 pb-6">
                    <DialogTitle className="text-2xl font-bold text-gray-900 text-center">
                      Share Your Requirements
                    </DialogTitle>
                    <DialogDescription className="text-gray-600 text-center text-lg">
                      Tell us what you're looking for and we'll provide
                      personalized assistance within 24 hours.
                    </DialogDescription>
                  </DialogHeader>
  
                  <div className="grid gap-6 py-4">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="contact-name" className="text-sm font-semibold text-gray-700">
                          Full Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contact-name"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="Enter your full name"
                          className="h-12 border border-gray-300 focus:ring-2 focus:ring-purple-500 rounded-lg transition-all"
                        />
                      </div>
  
                      <div className="space-y-2">
                        <Label htmlFor="contact-email" className="text-sm font-semibold text-gray-700">
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="your.email@example.com"
                          className="h-12 border border-gray-300 focus:ring-2 focus:ring-purple-500 rounded-lg transition-all"
                        />
                      </div>
                    </div>
  
                    <div className="space-y-2">
                      <Label htmlFor="contact-phone" className="text-sm font-semibold text-gray-700">
                        Phone Number
                      </Label>
                      <Input
                        id="contact-phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="h-12 border border-gray-300 focus:ring-2 focus:ring-purple-500 rounded-lg transition-all"
                      />
                    </div>
  
                    <div className="space-y-2">
                      <Label htmlFor="contact-message" className="text-sm font-semibold text-gray-700">
                        Your Requirements <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="contact-message"
                        value={contactForm.message}
                        onChange={(e) => {
                          if (e.target.value.length <= 300) {
                            setContactForm({ ...contactForm, message: e.target.value })
                          }
                        }}
                        placeholder="Enter the requirement what you are looking for?"
                        rows={4}
                        className="border border-gray-300 focus:ring-2 focus:ring-purple-500 rounded-lg resize-none transition-all"
                      />
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>Be as specific as possible to help us assist you better</span>
                        <span className="font-medium text-gray-600">{contactForm.message.length}/300</span>
                      </div>
                    </div>
                  </div>
  
                  <DialogFooter className="gap-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setIsContactFormOpen(false)}
                      disabled={isSubmitting}
                      className="px-6 py-3 rounded-lg font-semibold"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleContactSubmit}
                      disabled={!isContactFormValid || isSubmitting}
                      className="hbn-gradient text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Requirements
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
  
}
